import React from 'react';
import { Check, X, Plus } from 'lucide-react';

const comparisonData = [
  { service: 'Lawn mowing', lawnCare: 'included', ultimate: 'included' },
  { service: 'Edging / whipper snipping', lawnCare: 'included', ultimate: 'included' },
  { service: 'Blowing / clean-up', lawnCare: 'included', ultimate: 'included' },
  { service: 'Targeted weed control', lawnCare: 'included', ultimate: 'included' },
  { service: 'Seasonal lawn maintenance', lawnCare: 'included', ultimate: 'included' },
  { service: 'Garden bed management', lawnCare: 'included', ultimate: 'included' },
  { service: 'Gutter cleaning (1-2x/year)', lawnCare: 'not-included', ultimate: 'included' },
  { service: 'Internal window cleaning', lawnCare: 'not-included', ultimate: 'included' },
  { service: 'External window cleaning', lawnCare: 'not-included', ultimate: 'included' },
  { service: 'Window tracks & screens', lawnCare: 'not-included', ultimate: 'included' },
  { service: 'Hedge trimming', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'Pruning', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'Mulching', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'Fertilising', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'Pressure cleaning', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'House washing', lawnCare: 'addon', ultimate: 'addon' },
  { service: 'Solar panel cleaning', lawnCare: 'addon', ultimate: 'addon' },
];

const StatusIcon = ({ status }) => {
  if (status === 'included') {
    return <Check className="w-5 h-5 text-green-600" />;
  } else if (status === 'not-included') {
    return <X className="w-5 h-5 text-gray-300" />;
  } else {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
        <Plus className="w-3 h-3" />
        Add-on
      </span>
    );
  }
};

export default function PackageComparison() {
  return (
    <section id="comparison" className="py-16 bg-white" data-testid="package-comparison-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="font-oswald text-2xl sm:text-3xl font-bold text-green-900 mb-4">
            Package Comparison
          </h2>
          <div className="gold-accent-line w-20 mx-auto mb-4" />
          <p className="font-inter text-gray-600 max-w-xl mx-auto text-sm sm:text-base">
            Compare what's included in each package to find the right fit for your property.
          </p>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block overflow-hidden rounded-xl border border-gray-200 shadow-sm">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left font-oswald font-semibold text-gray-900 p-4 border-b border-gray-200">
                  Service
                </th>
                <th className="text-center font-oswald font-semibold text-green-900 p-4 border-b border-gray-200 bg-green-50">
                  <div className="flex flex-col items-center">
                    <span>Lawn Care</span>
                    <span className="text-xs font-inter font-normal text-green-600">Package</span>
                  </div>
                </th>
                <th className="text-center font-oswald font-semibold text-amber-900 p-4 border-b border-gray-200 bg-amber-50">
                  <div className="flex flex-col items-center">
                    <span>Ultimate Property</span>
                    <span className="text-xs font-inter font-normal text-amber-600">Package</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, idx) => (
                <tr 
                  key={idx} 
                  className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}
                >
                  <td className="font-inter text-sm text-gray-700 p-4 border-b border-gray-100">
                    {row.service}
                  </td>
                  <td className="text-center p-4 border-b border-gray-100 bg-green-50/30">
                    <div className="flex justify-center">
                      <StatusIcon status={row.lawnCare} />
                    </div>
                  </td>
                  <td className="text-center p-4 border-b border-gray-100 bg-amber-50/30">
                    <div className="flex justify-center">
                      <StatusIcon status={row.ultimate} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-6">
          {/* Lawn Care Package Card */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="bg-green-900 p-4 text-white">
              <h3 className="font-oswald text-lg font-semibold">Lawn Care Package</h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p className="font-oswald text-sm font-semibold text-green-800 mb-2">Included</p>
                <ul className="space-y-1">
                  {comparisonData.filter(r => r.lawnCare === 'included').map((row, idx) => (
                    <li key={idx} className="flex items-center gap-2 font-inter text-sm text-gray-700">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      {row.service}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <p className="font-oswald text-sm font-semibold text-amber-700 mb-2">Available as Add-ons</p>
                <div className="flex flex-wrap gap-1.5">
                  {comparisonData.filter(r => r.lawnCare === 'addon').map((row, idx) => (
                    <span key={idx} className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs font-inter">
                      {row.service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Ultimate Property Package Card */}
          <div className="bg-white rounded-xl border-2 border-amber-500 shadow-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-600 to-amber-500 p-4 text-white">
              <h3 className="font-oswald text-lg font-semibold">Ultimate Property Package</h3>
              <p className="font-inter text-xs text-white/80">Premium Complete Solution</p>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <p className="font-oswald text-sm font-semibold text-amber-800 mb-2">Included</p>
                <ul className="space-y-1">
                  {comparisonData.filter(r => r.ultimate === 'included').map((row, idx) => (
                    <li key={idx} className="flex items-center gap-2 font-inter text-sm text-gray-700">
                      <Check className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      {row.service}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-3 border-t border-gray-100">
                <p className="font-oswald text-sm font-semibold text-gray-600 mb-2">Available as Add-ons</p>
                <div className="flex flex-wrap gap-1.5">
                  {comparisonData.filter(r => r.ultimate === 'addon').map((row, idx) => (
                    <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-inter">
                      {row.service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm font-inter">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-gray-600">Included</span>
          </div>
          <div className="flex items-center gap-2">
            <X className="w-4 h-4 text-gray-300" />
            <span className="text-gray-600">Not included</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
              <Plus className="w-3 h-3" />
              Add-on
            </span>
            <span className="text-gray-600">Optional extra</span>
          </div>
        </div>
      </div>
    </section>
  );
}
